const LOCAL_UPLOAD_PREFIXES = [
  "uploads/",
  "products_image",
  "products/",
  "Vendor/",
  "vendor_documents/",
  "vendor_images/",
  "customer_profiles/",
  "category_images/",
  "category_icons/",
  "speciality_images/",
  "offers_images/",
  "Offers/",
  "offer/",
  "wall/",
  "quote_requests/",
];

const MEDIA_VERSION =
  (typeof window !== "undefined" && window.CONFIG && window.CONFIG.VERSION) ||
  "v1";

/**
 * Appends a version parameter for cache busting
 */
const applyVersion = (url) => {
  if (!url) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${MEDIA_VERSION}`;
};

/**
 * Gets the base URL of the site (e.g. http://localhost/locaso/)
 * by stripping the 'app/' part from global baseURL.
 */
const getSiteBaseURL = () => {
  const base = (globalThis.baseURL || "").replace(/\/+$/, "");
  if (base.endsWith("/app")) {
    return base.substring(0, base.length - 4) + "/";
  }
  return base + "/";
};

const getUploadBaseURL = () => {
  const configured =
    typeof window !== "undefined" &&
    window.CONFIG &&
    typeof window.CONFIG.UPLOAD_BASE_URL === "string"
      ? window.CONFIG.UPLOAD_BASE_URL
      : "";
  if (configured) {
    return configured.endsWith("/") ? configured : `${configured}/`;
  }
  // Fallback to siblings of 'app' folder
  return `${getSiteBaseURL()}uploads/`;
};

export const getFallbackImage = () =>
  applyVersion(`${getSiteBaseURL()}app/assets/default.png`);

/**
 * Resolves a dynamic image URL (products, vendors, categories)
 */
export const getImageUrl = (path) => {
  if (!path) return getFallbackImage();
  if (String(path).startsWith("http")) return applyVersion(path);

  let normalizedPath = String(path).replace(/^\/+/, "");
  if (!normalizedPath) return getFallbackImage();

  let resolvedUrl = "";
  // If path already contains 'uploads/', we just need to ensure the domain is present
  if (normalizedPath.startsWith("uploads/")) {
    resolvedUrl = `${getSiteBaseURL()}${normalizedPath}`;
  }
  // Check if it matches a known upload category
  else if (
    LOCAL_UPLOAD_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix))
  ) {
    resolvedUrl = `${getUploadBaseURL()}${normalizedPath}`;
  }
  // Default fallback for ambiguous paths
  else {
    resolvedUrl = `${getUploadBaseURL()}${normalizedPath}`;
  }

  return applyVersion(resolvedUrl);
};

/**
 * Resolves a vendor's primary business image or index-based image
 */
export const getVendorBusinessImageUrl = (imageValue, imageIndex = 1) => {
  if (!imageValue) return getFallbackImage();
  if (String(imageValue).startsWith("http")) return applyVersion(imageValue);

  const normalized = String(imageValue).replace(/^\/+/, "");

  // If it's a full path already, resolve it
  if (
    LOCAL_UPLOAD_PREFIXES.some((prefix) => normalized.startsWith(prefix)) ||
    /\.(png|jpe?g|gif|webp)$/i.test(normalized)
  ) {
    // Special case: if it contains a slash, it's likely a full path
    if (normalized.includes("/")) {
        return getImageUrl(normalized);
    }
  }

  // Otherwise assume it's a legacy ID-based image request
  return getImageUrl(
    `Vendor/Business_Images/${normalized}/BusinessImage${imageIndex}.png`,
  );
};

/**
 * Resolves a static UI asset URL
 */
export const getAssetUrl = (path) => {
  const cleanPath = String(path).replace(/^\/+/, "");
  return applyVersion(`${getSiteBaseURL()}app/${cleanPath}`);
};
